/* Centre d'aide Fonbex — vocation, premiers pas, guides par module.
 *
 * Design : page pédagogique accessible depuis la sidebar. Vocabulaire simple,
 * pas de jargon comptable, tout en français. Chaque module a une fiche courte
 * et un CTA direct vers la page.
 *
 * Structure :
 *   1. Vocation (ce que fait Fonbex)
 *   2. Premiers pas (checklist avec % d'avancement calculé côté client)
 *   3. Guides par module (grille de cartes filtrables par recherche)
 *   4. FAQ courte
 *
 * Aucune donnée n'est écrite en base ; les compteurs de la checklist viennent
 * des endpoints existants ``/companies``, ``/buildings``, ``/tenants``, ``/leases``,
 * ``/credits`` et ``/analysis``.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, CheckCircle2, Circle, ArrowRight, Building2, Home as HomeIcon,
  Users, KeyRound, FileText, CreditCard, TrendingUp, Calculator, Sparkles,
  CalendarClock, PackagePlus, MessageSquare, Compass, BookOpen,
} from "lucide-react";

const MODULE_GUIDES = [
  {
    id: "companies",
    icon: Building2,
    title: "Groupe & sociétés",
    tagline: "Vos SCI, holdings et sociétés d'exploitation en un seul endroit.",
    vocation: "Centralisez chaque entité juridique de votre patrimoine (SCI, SAS, holding, exploitation). Voyez d'un coup d'œil l'organigramme et les liens capitalistiques.",
    actions: [
      "Créer une société avec SIREN, forme juridique et régime fiscal",
      "Rattacher une société-mère pour construire l'organigramme",
      "Suivre les flux intra-groupe et les prêts entre sociétés",
    ],
    example: "Vous détenez une SCI qui possède 2 immeubles + une holding : créez d'abord la holding, puis les SCI en indiquant la holding comme mère.",
    pitfalls: [
      "Ne pas mélanger \"société\" (personne morale) et \"organisation\" (votre compte Fonbex)",
      "Le SIREN se compose de 9 chiffres sans espace",
    ],
    href: "/app/companies",
  },
  {
    id: "buildings",
    icon: HomeIcon,
    title: "Immeubles & biens",
    tagline: "Vos immeubles physiques et les lots qu'ils contiennent.",
    vocation: "Chaque immeuble regroupe un ou plusieurs lots (appartements, locaux, parkings). Vous consignez la valeur, l'adresse, les caractéristiques.",
    actions: [
      "Ajouter un immeuble avec adresse et valeur d'acquisition",
      "Créer les lots individuels (avec type, surface, loyer HC)",
      "Rattacher les lots à la société propriétaire",
    ],
    example: "Un immeuble à Lyon 3 avec 4 appartements : 1 immeuble + 4 lots. Chaque lot peut avoir un locataire différent.",
    pitfalls: [
      "Un lot ne peut pas exister sans immeuble parent",
      "La valeur d'acquisition doit inclure les frais de notaire pour la performance",
    ],
    href: "/app/buildings",
  },
  {
    id: "locations",
    icon: Users,
    title: "Locations & locataires",
    tagline: "Vos locataires, garants, baux et quittances.",
    vocation: "Ajoutez vos locataires (personnes physiques ou sociétés), leurs garants, et créez les baux qui lient un lot à un locataire.",
    actions: [
      "Créer un locataire avec ses coordonnées et revenus",
      "Ajouter un ou plusieurs garants (personnels ou Visale)",
      "Créer un bail (vide, meublé, commercial) avec loyer HC + charges",
      "Générer les quittances et suivre les paiements",
    ],
    example: "Marie loue votre appartement de 900 €/mois + 50 € de charges depuis 2023 : créez-la comme locataire, puis créez un bail relié au lot.",
    pitfalls: [
      "Un bail exige un locataire ET un lot — créez-les d'abord",
      "Sans e-mail, le locataire ne pourra pas accéder à son espace",
    ],
    href: "/app/locations",
  },
  {
    id: "inventories",
    icon: KeyRound,
    title: "États des lieux",
    tagline: "Entrée et sortie, avec photos et signatures.",
    vocation: "Consignez l'état d'un logement à l'entrée et à la sortie du locataire, pièce par pièce, avec photos et éventuelle signature.",
    actions: [
      "Créer un état des lieux d'entrée pour un bail existant",
      "Ajouter les pièces et leur condition (bon état, à revoir, dégradé)",
      "Joindre des photos et faire signer par les parties",
    ],
    example: "Nouveau locataire qui arrive dans un studio : créez un état des lieux d'entrée avec 3 pièces (entrée, séjour, salle de bain).",
    pitfalls: [
      "Sans état des lieux d'entrée, difficile de facturer des dégradations en sortie",
    ],
    href: "/app/inventories",
  },
  {
    id: "credits",
    icon: CreditCard,
    title: "Crédits",
    tagline: "Prêts immobiliers, tableau d'amortissement, simulations.",
    vocation: "Suivez chaque prêt (montant, taux, durée, assurance) avec son tableau d'amortissement, ses garanties et le capital restant dû.",
    actions: [
      "Ajouter un crédit (banque, montant, taux, durée)",
      "Rattacher le crédit à un immeuble et une société",
      "Consulter le tableau d'amortissement mois par mois",
      "Simuler un remboursement anticipé ou un changement de taux",
    ],
    example: "Prêt de 250 000 € sur 20 ans à 3,45 % pour votre immeuble Lyon 3 : ajoutez-le et le tableau se génère automatiquement.",
    pitfalls: [
      "Renseignez le taux d'assurance en % annuel du capital (ex : 0,36 %)",
      "La durée est en mois (240 mois = 20 ans)",
    ],
    href: "/app/credits",
  },
  {
    id: "performance",
    icon: TrendingUp,
    title: "Performance",
    tagline: "Rentabilité, cash-flow, TRI de chaque bien.",
    vocation: "Voyez la rentabilité brute et nette de chaque bien, le cash-flow mensuel après crédit, et l'évolution de votre patrimoine dans le temps.",
    actions: [
      "Comparer la rentabilité de vos biens",
      "Identifier les biens en cash-flow négatif",
      "Suivre l'évolution du patrimoine mois après mois",
    ],
    example: "Vous vous demandez si votre studio à Paris rapporte plus que votre 3-pièces à Lyon : la page Performance les compare instantanément.",
    pitfalls: [
      "Sans données de charges à jour, la rentabilité nette est approximative",
    ],
    href: "/app/performance",
  },
  {
    id: "accounting",
    icon: Calculator,
    title: "Comptabilité",
    tagline: "Recettes, dépenses, préparation de la déclaration.",
    vocation: "Regroupez vos recettes locatives et vos dépenses (travaux, charges, taxes) par société et par année. Les données sont prêtes pour votre expert-comptable.",
    actions: [
      "Consulter le compte de résultat par société",
      "Vérifier les documents à compléter",
      "Exporter les données pour votre comptable",
    ],
    example: "Fin d'année : votre expert-comptable veut le détail des dépenses de votre SCI — l'export CSV est prêt en 2 clics.",
    pitfalls: [
      "Un document non catégorisé n'apparaît pas dans le compte de résultat",
    ],
    href: "/app/accounting",
  },
  {
    id: "analysis",
    icon: Sparkles,
    title: "Analyse IA",
    tagline: "Importez un document, Fonbex le lit pour vous.",
    vocation: "Déposez un PDF (facture, quittance, avis d'impôt, tableau d'amortissement…) : l'IA extrait les données et les injecte dans les modules concernés.",
    actions: [
      "Importer une facture d'artisan → catégorisée en dépense",
      "Importer un avis de taxe foncière → charge annuelle",
      "Vérifier les champs extraits avant validation",
    ],
    example: "Vous recevez la facture du plombier par e-mail : glissez-la dans l'analyse IA, elle est catégorisée automatiquement.",
    pitfalls: [
      "Vérifiez toujours les données extraites avant validation",
      "Les PDF scannés de mauvaise qualité peuvent nécessiter une correction manuelle",
    ],
    href: "/app/analysis",
  },
  {
    id: "deadlines",
    icon: CalendarClock,
    title: "Échéances",
    tagline: "Toutes vos dates importantes en un seul calendrier.",
    vocation: "Fin de bail, révision de loyer, échéance de crédit, taxe foncière : recevez une alerte avant chaque échéance.",
    actions: [
      "Voir les échéances du mois",
      "Ajouter une échéance personnalisée",
      "Marquer une échéance comme traitée",
    ],
    example: "Votre bail expire dans 6 mois : Fonbex vous alerte pour anticiper la reconduction ou la reprise.",
    pitfalls: [
      "Une échéance sans propriétaire assigné peut être oubliée dans une équipe",
    ],
    href: "/app/deadlines",
  },
  {
    id: "mdb",
    icon: PackagePlus,
    title: "Marchand de biens",
    tagline: "Projets d'achat-revente, calculs de marge.",
    vocation: "Pour les activités MDB : suivez chaque opération (achat, travaux, revente) avec calcul de marge et de TVA sur marge.",
    actions: [
      "Créer une opération MDB avec le prix d'achat",
      "Ajouter les postes de travaux",
      "Estimer la marge nette après revente",
    ],
    example: "Achat 200 000 €, travaux 50 000 €, revente estimée 320 000 € : la marge nette et la TVA sur marge sont calculées instantanément.",
    pitfalls: [
      "Ne pas oublier les frais de notaire d'achat dans le coût de revient",
    ],
    href: "/app/mdb",
  },
  {
    id: "beta",
    icon: MessageSquare,
    title: "Programme bêta",
    tagline: "Vos avantages fondateur et vos retours utilisateurs.",
    vocation: "En tant que bêta-testeur, vous bénéficiez de 50 % de remise permanente. Le widget \"Feedback\" (icône en bas à gauche) permet de nous remonter tout ce que vous voyez.",
    actions: [
      "Envoyer un bug, une amélioration ou une question",
      "Consulter vos retours passés",
      "Suivre les résolutions",
    ],
    example: "Un bouton mal placé sur mobile ? Un mot mal choisi ? Signalez-le en 30 secondes.",
    pitfalls: [],
    href: "/app/settings?tab=beta",
  },
  {
    id: "tenant-portal",
    icon: HomeIcon,
    title: "Espace locataire",
    tagline: "Un accès dédié pour vos locataires.",
    vocation: "Chaque locataire dispose de son propre espace pour consulter son bail, télécharger ses quittances, envoyer des documents et vous adresser des messages.",
    actions: [
      "Inviter un locataire depuis sa fiche (bouton \"Inviter dans l'espace locataire\")",
      "Suivre le statut de l'invitation (envoyée / activée / expirée)",
      "Consulter les documents déposés",
      "Répondre aux messages et aux demandes d'intervention",
    ],
    example: "Marie doit vous envoyer son attestation d'assurance : au lieu d'un e-mail, elle la dépose dans son espace, vous la validez en 1 clic.",
    pitfalls: [
      "Sans e-mail sur la fiche locataire, l'invitation est impossible",
      "Le lien d'invitation expire au bout de 7 jours",
    ],
    href: "/app/locations",
  },
];

const FAQ = [
  { q: "Comment créer ma première société ?",
    a: "Menu Groupe & sociétés → bouton \"Créer une société\". Renseignez la dénomination, la forme juridique (SCI, SAS...) et le SIREN si vous l'avez." },
  { q: "Puis-je gérer plusieurs organisations séparées ?",
    a: "Oui — vous pouvez créer plusieurs organisations (ex : patrimoine perso + patrimoine parents) et basculer de l'une à l'autre depuis le sélecteur en haut à gauche." },
  { q: "Mes données sont-elles isolées des autres utilisateurs ?",
    a: "Oui. Fonbex est strictement multi-tenant : chaque organisation est isolée. Personne ne peut voir vos données sans y être invité." },
  { q: "Comment inviter un collaborateur ?",
    a: "Menu Paramètres → onglet Membres → \"Inviter\". Vous choisissez son rôle (propriétaire, gestionnaire, collaborateur, lecture seule)." },
  { q: "L'IA se trompe sur une facture — que faire ?",
    a: "Cliquez sur \"Corriger\" avant de valider : chaque correction améliore l'apprentissage. Nous ne validons rien sans votre accord." },
  { q: "Puis-je annuler mon abonnement à tout moment ?",
    a: "Oui, sans engagement. Vos données restent accessibles 30 jours après annulation pour export." },
];

export default function HelpPage() {
  const [q, setQ] = useState("");
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, b, t, cr, a] = await Promise.all([
          api.get("/companies").then((r) => r.data.items || []).catch(() => []),
          api.get("/buildings").then((r) => r.data.items || []).catch(() => []),
          api.get("/tenants").then((r) => r.data.items || []).catch(() => []),
          api.get("/credits").then((r) => r.data.items || []).catch(() => []),
          api.get("/analysis").then((r) => r.data.items || []).catch(() => []),
        ]);
        const leases = await api.get("/leases").then((r) => r.data.items || []).catch(() => []);
        setProgress({
          companies: c.length,
          buildings: b.length,
          lots: b.reduce((s, x) => s + (x.lots_count || 0), 0),
          tenants: t.length,
          leases: leases.length,
          credits: cr.length,
          documents: a.length,
        });
      } catch {
        setProgress({ companies: 0, buildings: 0, lots: 0, tenants: 0, leases: 0, credits: 0, documents: 0 });
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return MODULE_GUIDES;
    const needle = q.trim().toLowerCase();
    return MODULE_GUIDES.filter((m) =>
      m.title.toLowerCase().includes(needle)
      || m.tagline.toLowerCase().includes(needle)
      || m.vocation.toLowerCase().includes(needle)
      || m.actions.some((s) => s.toLowerCase().includes(needle))
      || m.example.toLowerCase().includes(needle),
    );
  }, [q]);

  return (
    <div className="animate-fade-up" data-testid="help-page">
      <PageHeader
        overline="Centre d'aide"
        title="Bien démarrer avec Fonbex"
        description="Comprenez ce que fait chaque module, suivez la checklist des premiers pas et retrouvez les guides essentiels."
      />

      <Tabs defaultValue="start">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="start" data-testid="help-tab-start">
            <Compass className="h-3.5 w-3.5 mr-1.5" /> Premiers pas
          </TabsTrigger>
          <TabsTrigger value="modules" data-testid="help-tab-modules">
            <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Guides par module
          </TabsTrigger>
          <TabsTrigger value="faq" data-testid="help-tab-faq">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> FAQ
          </TabsTrigger>
        </TabsList>

        <TabsContent value="start">
          <VocationCard />
          <ProgressChecklist progress={progress} />
        </TabsContent>

        <TabsContent value="modules">
          <div className="mb-4 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher : crédit, bail, facture, locataire…"
              className="pl-9"
              data-testid="help-search"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-sm text-muted-foreground py-12">
                Aucun guide ne correspond à &quot;{q}&quot;.
              </div>
            )}
            {filtered.map((m) => <ModuleCard key={m.id} module={m} />)}
          </div>
        </TabsContent>

        <TabsContent value="faq">
          <div className="space-y-3 max-w-3xl">
            {FAQ.map((item) => (
              <details key={item.q} className="card-elevated p-4 group" data-testid={`faq-${item.q.slice(0, 20)}`}>
                <summary className="cursor-pointer font-heading text-sm flex items-center justify-between gap-3">
                  {item.q}
                  <ArrowRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function VocationCard() {
  return (
    <section
      className="rounded-2xl p-6 lg:p-8 mb-8 relative overflow-hidden text-white"
      style={{ background: "hsl(158 47% 12%)" }}
      data-testid="help-vocation"
    >
      <div className="relative z-10 max-w-3xl">
        <div className="text-[10px] uppercase tracking-widest text-accent mb-2">Vocation</div>
        <h2 className="font-serif text-2xl sm:text-3xl">
          Fonbex centralise, structure et éclaire votre patrimoine immobilier.
        </h2>
        <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/85">
          <li>• Centraliser sociétés, SCI et patrimoine</li>
          <li>• Gérer immeubles, lots, locations et locataires</li>
          <li>• Suivre crédits, rentabilité et trésorerie</li>
          <li>• Analyser les documents avec l&apos;IA</li>
          <li>• Préparer les données comptables</li>
          <li>• Suivre les échéances importantes</li>
          <li>• Piloter les projets de marchand de biens</li>
          <li>• Bénéficier d&apos;un copilote patrimonial (progressif)</li>
        </ul>
      </div>
      <div className="absolute inset-0 opacity-30"
           style={{ background: "radial-gradient(circle at 85% 10%, hsl(36 43% 50% / .4), transparent 55%)" }} />
    </section>
  );
}

function ProgressChecklist({ progress }) {
  const steps = useMemo(() => {
    const p = progress || {};
    return [
      { key: "profile", label: "Compléter votre profil", done: true, hint: "Nom, email vérifié, préférences de notification." , href: "/app/settings" },
      { key: "company", label: "Créer votre première société ou SCI", done: (p.companies || 0) > 0, href: "/app/companies" },
      { key: "building", label: "Ajouter un immeuble", done: (p.buildings || 0) > 0, href: "/app/buildings" },
      { key: "lot", label: "Créer un lot dans l'immeuble", done: (p.lots || 0) > 0, href: "/app/buildings" },
      { key: "tenant", label: "Ajouter un locataire", done: (p.tenants || 0) > 0, href: "/app/locations" },
      { key: "lease", label: "Créer un bail", done: (p.leases || 0) > 0, href: "/app/locations" },
      { key: "credit", label: "Ajouter un crédit", done: (p.credits || 0) > 0, href: "/app/credits" },
      { key: "doc", label: "Importer un premier document", done: (p.documents || 0) > 0, href: "/app/analysis" },
      { key: "invite", label: "Inviter un locataire dans son espace", done: false, href: "/app/locations", optional: true },
      { key: "pilot", label: "Consulter le centre de pilotage", done: false, href: "/app/dashboard" },
    ];
  }, [progress]);

  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / total) * 100);

  return (
    <section className="card-elevated p-6 lg:p-8" data-testid="help-checklist">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-overline mb-1">Checklist</div>
          <h2 className="font-heading text-2xl">Vos premiers pas sur Fonbex</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-lg">
            Suivez les étapes ci-dessous pour prendre en main l&apos;application. Pas besoin de tout faire d&apos;un coup — le rythme est le vôtre.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Progression</div>
          <div className="mt-1 font-serif text-4xl text-primary" data-testid="help-progress-pct">{pct}%</div>
          <div className="text-xs text-muted-foreground">{done} / {total} étapes</div>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-6 space-y-2">
        {steps.map((s) => (
          <li key={s.key}>
            <Link
              to={s.href}
              data-testid={`checklist-step-${s.key}`}
              className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-secondary/40 transition-colors"
            >
              {s.done ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className={`text-sm ${s.done ? "text-muted-foreground line-through" : "font-medium"}`}>
                  {s.label}
                  {s.optional && <span className="ml-2 text-xs text-muted-foreground">(optionnel)</span>}
                </div>
                {s.hint && !s.done && <div className="text-xs text-muted-foreground mt-0.5">{s.hint}</div>}
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ModuleCard({ module }) {
  const Icon = module.icon || FileText;
  return (
    <article
      className="card-elevated p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
      data-testid={`help-module-${module.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/15 flex-shrink-0">
          <Icon className="h-4 w-4 text-accent-foreground" />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-base leading-tight">{module.title}</h3>
          <p className="text-xs text-muted-foreground truncate">{module.tagline}</p>
        </div>
      </div>

      <p className="text-sm leading-relaxed">{module.vocation}</p>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Actions principales</div>
        <ul className="text-xs space-y-1">
          {module.actions.map((a) => <li key={a}>• {a}</li>)}
        </ul>
      </div>

      <div className="rounded-lg bg-secondary/40 p-3 text-xs">
        <div className="font-medium mb-1">Exemple concret</div>
        <p className="text-muted-foreground">{module.example}</p>
      </div>

      {module.pitfalls.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
            Erreurs fréquentes ({module.pitfalls.length})
          </summary>
          <ul className="mt-2 space-y-1 text-muted-foreground">
            {module.pitfalls.map((p) => <li key={p}>⚠ {p}</li>)}
          </ul>
        </details>
      )}

      <div className="mt-auto pt-2">
        <Button asChild variant="outline" size="sm" className="w-full">
          <Link to={module.href} data-testid={`help-goto-${module.id}`}>
            Ouvrir ce module <ArrowRight className="ml-2 h-3 w-3" />
          </Link>
        </Button>
      </div>
    </article>
  );
}
